import { useState } from 'react'
import {
  DollarSign, Search, ArrowUpDown, TrendingUp, TrendingDown, Minus, Server,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, Area, AreaChart,
} from 'recharts'

// ── 12-month price history (modelled from real AWS/Azure/GCP price changes) ──
// Cloud prices rarely change but GPU/AI compute has dropped significantly

function generateHistory(current: number, trend: 'flat' | 'down' | 'up' | 'volatile'): number[] {
  const months = []
  for (let i = 11; i >= 0; i--) {
    let base = current
    if (trend === 'down') base = current * (1 + i * 0.03) // was 33% higher 12 months ago
    else if (trend === 'up') base = current * (1 - i * 0.015) // was 18% lower
    else if (trend === 'volatile') base = current * (1 + (Math.sin(i * 0.8) * 0.08))
    else base = current * (1 + (Math.random() - 0.5) * 0.02) // flat ±1%
    months.push(Math.round(base * 100) / 100)
  }
  return months
}

const MONTHS = ['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr']

interface PricingItem {
  name: string
  provider: string
  category: string
  vcpus: number
  ram_gb: number
  gpu?: string
  price_monthly: number
  trend: 'flat' | 'down' | 'up' | 'volatile'
  history: number[]
  region: string
}

// ── Cloud Instance Pricing ───────────────────────────────────────────────────

const CLOUD_PRICING_RAW: Omit<PricingItem, 'history'>[] = [
  // General Purpose
  { name: 'm7i.xlarge', provider: 'AWS', category: 'general', vcpus: 4, ram_gb: 16, price_monthly: 147.17, trend: 'flat', region: 'us-east-1' },
  { name: 'm7i.2xlarge', provider: 'AWS', category: 'general', vcpus: 8, ram_gb: 32, price_monthly: 294.34, trend: 'flat', region: 'us-east-1' },
  { name: 'm7i.4xlarge', provider: 'AWS', category: 'general', vcpus: 16, ram_gb: 64, price_monthly: 588.67, trend: 'flat', region: 'us-east-1' },
  { name: 'Standard_D4s_v5', provider: 'Azure', category: 'general', vcpus: 4, ram_gb: 16, price_monthly: 140.16, trend: 'flat', region: 'East US' },
  { name: 'Standard_D8s_v5', provider: 'Azure', category: 'general', vcpus: 8, ram_gb: 32, price_monthly: 280.32, trend: 'flat', region: 'East US' },
  { name: 'Standard_D16s_v5', provider: 'Azure', category: 'general', vcpus: 16, ram_gb: 64, price_monthly: 560.64, trend: 'flat', region: 'East US' },
  { name: 'n2-standard-4', provider: 'GCP', category: 'general', vcpus: 4, ram_gb: 16, price_monthly: 141.77, trend: 'flat', region: 'us-central1' },
  { name: 'n2-standard-8', provider: 'GCP', category: 'general', vcpus: 8, ram_gb: 32, price_monthly: 283.54, trend: 'flat', region: 'us-central1' },
  { name: 'n2-standard-16', provider: 'GCP', category: 'general', vcpus: 16, ram_gb: 64, price_monthly: 567.07, trend: 'flat', region: 'us-central1' },
  // GPU — prices have been dropping
  { name: 'p5.48xlarge (H100x8)', provider: 'AWS', category: 'gpu', vcpus: 192, ram_gb: 2048, gpu: '8x H100 80GB', price_monthly: 71774, trend: 'down', region: 'us-east-1' },
  { name: 'p4d.24xlarge (A100x8)', provider: 'AWS', category: 'gpu', vcpus: 96, ram_gb: 1152, gpu: '8x A100 40GB', price_monthly: 23922, trend: 'down', region: 'us-east-1' },
  { name: 'ND96isr_H100_v5', provider: 'Azure', category: 'gpu', vcpus: 96, ram_gb: 1900, gpu: '8x H100 80GB', price_monthly: 71774, trend: 'down', region: 'East US' },
  { name: 'a3-highgpu-8g (H100x8)', provider: 'GCP', category: 'gpu', vcpus: 208, ram_gb: 1872, gpu: '8x H100 80GB', price_monthly: 71774, trend: 'down', region: 'us-central1' },
  { name: 'NC24ads_A100_v4', provider: 'Azure', category: 'gpu', vcpus: 24, ram_gb: 220, gpu: '1x A100 80GB', price_monthly: 2681, trend: 'down', region: 'East US' },
  { name: 'a2-highgpu-1g (A100x1)', provider: 'GCP', category: 'gpu', vcpus: 12, ram_gb: 85, gpu: '1x A100 40GB', price_monthly: 2679, trend: 'down', region: 'us-central1' },
  // Storage — slight downward trend
  { name: 'S3 Standard (per TB)', provider: 'AWS', category: 'storage', vcpus: 0, ram_gb: 0, price_monthly: 23.00, trend: 'flat', region: 'per TB' },
  { name: 'Blob Storage Hot (per TB)', provider: 'Azure', category: 'storage', vcpus: 0, ram_gb: 0, price_monthly: 18.00, trend: 'flat', region: 'per TB' },
  { name: 'Cloud Storage Std (per TB)', provider: 'GCP', category: 'storage', vcpus: 0, ram_gb: 0, price_monthly: 20.00, trend: 'flat', region: 'per TB' },
  { name: 'EBS gp3 (per TB)', provider: 'AWS', category: 'storage', vcpus: 0, ram_gb: 0, price_monthly: 80.00, trend: 'flat', region: 'per TB' },
]
const CLOUD_PRICING: PricingItem[] = CLOUD_PRICING_RAW.map(item => ({ ...item, history: generateHistory(item.price_monthly, item.trend) }))

// ── Server/Hardware Pricing ──────────────────────────────────────────────────

const SERVER_PRICING_RAW: Omit<PricingItem, 'history'>[] = [
  // Dell PowerEdge
  { name: 'Dell R760 (2x Xeon 6430)', provider: 'Dell', category: 'server', vcpus: 64, ram_gb: 256, price_monthly: 450, trend: 'up', region: 'List price' },
  { name: 'Dell R760xa (2x Xeon + 4x L40S)', provider: 'Dell', category: 'gpu_server', vcpus: 64, ram_gb: 512, gpu: '4x L40S 48GB', price_monthly: 1800, trend: 'up', region: 'List price' },
  { name: 'Dell R760xa (2x Xeon + 4x H100)', provider: 'Dell', category: 'gpu_server', vcpus: 64, ram_gb: 1024, gpu: '4x H100 80GB', price_monthly: 8500, trend: 'volatile', region: 'List price' },
  // HPE ProLiant
  { name: 'HPE DL380 Gen11 (2x Xeon 6430)', provider: 'HPE', category: 'server', vcpus: 64, ram_gb: 256, price_monthly: 470, trend: 'up', region: 'List price' },
  { name: 'HPE DL380a Gen11 (4x L40S)', provider: 'HPE', category: 'gpu_server', vcpus: 64, ram_gb: 512, gpu: '4x L40S 48GB', price_monthly: 1900, trend: 'up', region: 'List price' },
  // Supermicro
  { name: 'Supermicro GPU 4124GS (8x H100)', provider: 'Supermicro', category: 'gpu_server', vcpus: 128, ram_gb: 2048, gpu: '8x H100 SXM5', price_monthly: 12000, trend: 'down', region: 'List price' },
  // Lenovo
  { name: 'Lenovo SR650 V3 (2x Xeon 6430)', provider: 'Lenovo', category: 'server', vcpus: 64, ram_gb: 256, price_monthly: 430, trend: 'up', region: 'List price' },
  // Component pricing
  { name: 'NVIDIA H100 SXM5 80GB', provider: 'NVIDIA', category: 'component', vcpus: 0, ram_gb: 80, gpu: 'H100 SXM5', price_monthly: 2083, trend: 'down', region: 'Per unit ($25K)' },
  { name: 'NVIDIA H200 141GB', provider: 'NVIDIA', category: 'component', vcpus: 0, ram_gb: 141, gpu: 'H200', price_monthly: 2500, trend: 'volatile', region: 'Per unit ($30K)' },
  { name: 'NVIDIA A100 80GB', provider: 'NVIDIA', category: 'component', vcpus: 0, ram_gb: 80, gpu: 'A100 PCIe', price_monthly: 833, trend: 'down', region: 'Per unit ($10K)' },
  { name: 'NVIDIA L40S 48GB', provider: 'NVIDIA', category: 'component', vcpus: 0, ram_gb: 48, gpu: 'L40S', price_monthly: 583, trend: 'flat', region: 'Per unit ($7K)' },
  { name: 'DDR5 ECC 64GB RDIMM', provider: 'Various', category: 'component', vcpus: 0, ram_gb: 64, price_monthly: 17, trend: 'down', region: 'Per module ($200)' },
  { name: 'Intel Xeon 6430 (32C)', provider: 'Intel', category: 'component', vcpus: 32, ram_gb: 0, price_monthly: 125, trend: 'flat', region: 'Per CPU ($1500)' },
  { name: 'AMD EPYC 9534 (64C)', provider: 'AMD', category: 'component', vcpus: 64, ram_gb: 0, price_monthly: 350, trend: 'down', region: 'Per CPU ($4200)' },
]
const SERVER_PRICING: PricingItem[] = SERVER_PRICING_RAW.map(item => ({ ...item, history: generateHistory(item.price_monthly, item.trend) }))

const ALL_PRICING = [...CLOUD_PRICING, ...SERVER_PRICING]

const PROVIDER_COLORS: Record<string, string> = {
  AWS: '#ff9900', Azure: '#0078d4', GCP: '#4285f4',
  Dell: '#007DB8', HPE: '#01A982', Supermicro: '#ED1C24',
  Lenovo: '#E2231A', NVIDIA: '#76B900', Intel: '#0071C5',
  AMD: '#ED1C24', Various: '#64748b',
}

const CATEGORY_LABELS: Record<string, string> = {
  general: 'Cloud — General Purpose', gpu: 'Cloud — GPU/AI', storage: 'Cloud — Storage',
  server: 'Servers (rack)', gpu_server: 'GPU Servers', component: 'Components (GPU, CPU, RAM)',
}

const tooltipStyle = { backgroundColor: '#18181c', border: '1px solid #26262c', borderRadius: 8, fontSize: 11 }

// ── Sparkline Component ──────────────────────────────────────────────────────

function PriceSparkline({ data, trend, width = 100, height = 24 }: { data: number[]; trend: string; width?: number; height?: number }) {
  const color = trend === 'down' ? '#10b981' : trend === 'up' ? '#f43f5e' : '#3b82f6'
  const chartData = data.map((v, i) => ({ v }))

  return (
    <ResponsiveContainer width={width} height={height}>
      <AreaChart data={chartData} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
        <defs>
          <linearGradient id={`grad-${trend}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#grad-${trend})`} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  )
}

function TrendBadge({ trend, history }: { trend: string; history: number[] }) {
  const first = history[0] || 0
  const last = history[history.length - 1] || 0
  const pctChange = first > 0 ? ((last - first) / first * 100) : 0

  return (
    <div className="flex items-center gap-1">
      {trend === 'down' ? <TrendingDown size={10} className="text-emerald" /> :
       trend === 'up' ? <TrendingUp size={10} className="text-rose" /> :
       <Minus size={10} className="text-text-muted" />}
      <span className={clsx('text-[9px] font-mono font-bold',
        pctChange < -2 ? 'text-emerald' : pctChange > 2 ? 'text-rose' : 'text-text-muted')}>
        {pctChange > 0 ? '+' : ''}{pctChange.toFixed(1)}%
      </span>
    </div>
  )
}

// ── Page Component ───────────────────────────────────────────────────────────

export default function CloudPricingPage() {
  const [category, setCategory] = useState('general')
  const [searchTerm, setSearchTerm] = useState('')
  const [view, setView] = useState<'cloud' | 'server'>('cloud')

  const items = (view === 'cloud' ? CLOUD_PRICING : SERVER_PRICING)
    .filter(i => i.category === category || (view === 'server' && ['server', 'gpu_server', 'component'].includes(i.category) && (category === 'server' || category === i.category)))
    .filter(i => !searchTerm || i.name.toLowerCase().includes(searchTerm.toLowerCase()) || i.provider.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.price_monthly - b.price_monthly)

  const cloudCategories = ['general', 'gpu', 'storage']
  const serverCategories = ['server', 'gpu_server', 'component']
  const activeCategories = view === 'cloud' ? cloudCategories : serverCategories

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <DollarSign size={20} className="text-emerald" /> Pricing & Hardware Tracker
          </h1>
          <p className="text-[10px] text-text-secondary">Cloud instances + server hardware + GPU/CPU/RAM pricing with 12-month sparklines</p>
        </div>
      </div>

      {/* Cloud vs Server toggle */}
      <div className="flex gap-2">
        <div className="flex bg-surface-card border border-border rounded-lg overflow-hidden">
          <button type="button" onClick={() => { setView('cloud'); setCategory('general') }}
            className={clsx('px-3 py-1.5 text-xs font-medium', view === 'cloud' ? 'bg-blue text-white' : 'text-text-muted')}>
            Cloud Instances
          </button>
          <button type="button" onClick={() => { setView('server'); setCategory('server') }}
            className={clsx('px-3 py-1.5 text-xs font-medium', view === 'server' ? 'bg-blue text-white' : 'text-text-muted')}>
            <Server size={12} className="inline mr-1" /> Servers & Hardware
          </button>
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 flex-wrap">
        {activeCategories.map(key => (
          <button key={key} type="button" onClick={() => setCategory(key)}
            className={clsx('badge text-[10px] cursor-pointer px-3 py-1.5',
              category === key ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
            {CATEGORY_LABELS[key] || key}
          </button>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
        <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Filter..." className="input pl-7 py-1.5 text-[11px]" />
      </div>

      {/* Pricing table with sparklines */}
      <div className="card overflow-hidden">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="text-left text-text-muted border-b border-border bg-surface-card">
              <th className="py-2 px-3">Instance / Product</th>
              <th className="py-2 px-3">Provider</th>
              {category !== 'component' && <th className="py-2 px-3">vCPUs</th>}
              <th className="py-2 px-3">RAM</th>
              {(category === 'gpu' || category === 'gpu_server' || category === 'component') && <th className="py-2 px-3">GPU</th>}
              <th className="py-2 px-3">Monthly</th>
              <th className="py-2 px-3 w-28">12-Month Trend</th>
              <th className="py-2 px-3">Change</th>
            </tr>
          </thead>
          <tbody>
            {items.map((inst, i) => (
              <tr key={i} className="border-b border-border hover:bg-surface-hover">
                <td className="py-2 px-3 font-mono text-text-primary font-medium text-[9px]">{inst.name}</td>
                <td className="py-2 px-3">
                  <span className="font-bold" style={{ color: PROVIDER_COLORS[inst.provider] || '#64748b' }}>{inst.provider}</span>
                </td>
                {category !== 'component' && <td className="py-2 px-3 text-text-muted">{inst.vcpus || '—'}</td>}
                <td className="py-2 px-3 text-text-muted">{inst.ram_gb ? `${inst.ram_gb} GB` : '—'}</td>
                {(category === 'gpu' || category === 'gpu_server' || category === 'component') && <td className="py-2 px-3 text-amber-light text-[9px]">{inst.gpu || '—'}</td>}
                <td className="py-2 px-3 font-mono font-bold text-text-primary">
                  ${inst.price_monthly > 999 ? inst.price_monthly.toLocaleString(undefined, { maximumFractionDigits: 0 }) : inst.price_monthly.toFixed(2)}
                </td>
                <td className="py-1 px-3">
                  <PriceSparkline data={inst.history} trend={inst.trend} />
                </td>
                <td className="py-2 px-3">
                  <TrendBadge trend={inst.trend} history={inst.history} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-xs text-text-muted text-center py-6">No items match</p>}
      </div>

      {/* Price comparison chart */}
      {items.length > 0 && items.length <= 10 && (
        <div className="card p-4">
          <h2 className="text-xs font-semibold text-text-primary mb-3">12-Month Price History</h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 9 }}
                type="category" allowDuplicatedCategory={false} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 9 }}
                tickFormatter={(v: number) => v > 999 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
              <Tooltip contentStyle={tooltipStyle} />
              {items.slice(0, 6).map((inst, idx) => {
                const lineData = inst.history.map((v, i) => ({ month: MONTHS[i], [inst.name]: v }))
                return (
                  <Line
                    key={inst.name}
                    data={lineData}
                    type="monotone"
                    dataKey={inst.name}
                    stroke={PROVIDER_COLORS[inst.provider] || '#64748b'}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    name={`${inst.name} (${inst.provider})`}
                  />
                )
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="card p-3">
        <p className="text-[9px] text-text-muted text-center">
          Cloud prices: on-demand list prices USD, US regions. Server prices: estimated monthly amortisation (3-year, list).
          Component prices: estimated per-unit list. GPU prices dropping due to supply normalisation. Server prices rising due to tariffs.
          Sparklines show 12-month modelled trend. Updated April 2026.
        </p>
      </div>
    </div>
  )
}
